import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import { SUPPORTED_CHAIN_IDS_LIMIT_ORDERS } from '../constants/chain';
import { REFRESH_INTERVAL } from '../constants/rpc';
import { LimitOrderResponse } from '../utils/0x.types';
import { fetcherWithJwt } from '../utils/fetcher';
import type { ChainAddress } from '../utils/models';
import { routes } from '../utils/routes';
import { objectToQueryString } from '../utils/string';

export type LimitOrdersQueryArgs = {
  searchTerm?: string;
  chainId?: number;
  makers?: string;
  makerToken?: ChainAddress;
  takerToken?: ChainAddress;
  /** Comma separated `address:chainId` scope entries (see tokenKey). */
  tokens?: string;
  pairOnly?: boolean;
  page: number;
  limit: number;
};

type UseLimitOrdersArgs = Omit<LimitOrdersQueryArgs, 'makers'> & {
  makers?: readonly ChainAddress[];
  options?: Omit<UseQueryOptions<LimitOrderResponse | undefined>, 'queryKey' | 'queryFn'>;
};

export const limitOrdersQueryKey = (args: LimitOrdersQueryArgs | undefined) =>
  ['limit-orders', args] as const;

export const useLimitOrders = ({
  searchTerm,
  chainId,
  makerToken,
  takerToken,
  tokens,
  pairOnly,
  page,
  limit,
  makers,
  options,
}: UseLimitOrdersArgs): UseQueryResult<LimitOrderResponse | undefined> => {
  const queryArgs: LimitOrdersQueryArgs | undefined =
    makers && makers.length > 0 && (!chainId || SUPPORTED_CHAIN_IDS_LIMIT_ORDERS.includes(chainId))
      ? {
          searchTerm,
          chainId,
          makers: makers.join(','),
          makerToken,
          takerToken,
          tokens,
          ...(pairOnly ? { pairOnly } : {}),
          page,
          limit,
        }
      : undefined;

  return useQuery<LimitOrderResponse | undefined>({
    queryKey: limitOrdersQueryKey(queryArgs),
    queryFn: () => fetcherWithJwt(`${routes.api.LIMIT_ORDERS}${objectToQueryString(queryArgs!)}`),
    refetchInterval: REFRESH_INTERVAL,
    refetchOnMount: 'always',
    ...options,
    enabled: !!queryArgs && (options?.enabled ?? true),
  });
};
